import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Backdrop,
  CircularProgress,
  Fade,
  Grid,
  Icon,
  LinearProgress,
  Tab,
  Tabs,
} from '@mui/material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import { useForm } from 'react-hook-form';
import { Equalizer, Settings } from '@mui/icons-material';
import { decryptURL, CustomPagination } from 'utils/functions';
import { DropsContext } from 'contexts/DropsContext';
import { useDialog } from 'components/Modals';
import VirtualDrop from 'components/VirtualDrop';
import Container from 'components/Container';
import Button from 'components/Button';
import Header from 'components/Header';
import Card from 'components/Card';
import api from 'services/api';
import localeText from 'utils/localeText';
import styles from './styles';

const EditModal = ({ item, anuncio, drops, onSubmit }) => {
  const defaultValues = {
    produto_id: item?.produto_id || null,
  };
  const { control, handleSubmit } = useForm({ defaultValues });

  const onConfirm = (values) => {
    const data = {
      id: anuncio?.id,
      produto_id: values?.produto_id || null,
      variacao_id: item?.id || null,
    };

    onSubmit(data);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <VirtualDrop
          name="produto_id"
          control={control}
          label="Produto"
          options={drops?.Produto?.filter((f) =>
            [
              '00-Mercadoria para revenda',
              '04-Produto acabado',
              '05-Subproduto',
              '06-Produto intermediário',
            ]?.includes(f?.tipolinha)
          )}
        />
      </Grid>
      <Grid item xs={12} textAlign="center">
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit(onConfirm)}
        >
          Salvar
        </Button>
      </Grid>
    </Grid>
  );
};

const Anuncio = ({ anuncio, drops, loading, onSubmit }) => {
  const defaultValues = {
    id: anuncio?.id,
    produto_id: anuncio?.produto_id || null,
  };
  const { control, handleSubmit } = useForm({ defaultValues });

  return (
    <Card title="Informações Gerais">
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <VirtualDrop
            name="produto_id"
            control={control}
            label="Produto"
            options={drops?.Produto?.filter((f) =>
              [
                '00-Mercadoria para revenda',
                '04-Produto acabado',
                '05-Subproduto',
                '06-Produto intermediário',
              ]?.includes(f?.tipolinha)
            )}
          />
        </Grid>
        <Grid item xs={12} textAlign="center">
          <Button
            color="primary"
            loading={loading}
            variant="contained"
            onClick={handleSubmit(onSubmit)}
          >
            Salvar
          </Button>
        </Grid>
      </Grid>
    </Card>
  );
};

const Variacoes = ({ anuncio, drops, loading, onSubmit }) => {
  const { openDialog } = useDialog();

  return (
    <Card title="Variações">
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <DataGrid
            rows={anuncio?.variacoes || []}
            columns={[
              {
                field: 'produto_descricao',
                headerName: 'Produto',
                minWidth: 300,
                flex: 1,
                sortable: false,
              },
              {
                field: 'sku',
                headerName: 'SKU',
                width: 300,
                sortable: false,
                valueGetter: ({ row }) => row?.detalhes?.sku,
              },
              {
                field: 'preco',
                headerName: 'Preço',
                width: 200,
                sortable: false,
                type: 'number',
                valueGetter: ({ value }) =>
                  value &&
                  value?.toLocaleString('pt-br', {
                    style: 'currency',
                    currency: 'BRL',
                  }),
              },
              {
                field: 'estoque',
                headerName: 'Saldo',
                width: 200,
                type: 'number',
                sortable: false,
              },
              {
                field: 'actions',
                headerName: 'Ações',
                type: 'actions',
                width: 100,
                getActions: ({ row }) => [
                  <GridActionsCellItem
                    icon={<Icon>edit</Icon>}
                    label="Editar"
                    onClick={() =>
                      openDialog(
                        <EditModal
                          item={row}
                          drops={drops}
                          anuncio={anuncio}
                          onSubmit={onSubmit}
                        />,
                        'Editar Variação'
                      )
                    }
                  />,
                ],
              },
            ]}
            autoHeight
            pagination
            pageSize={10}
            density="compact"
            loading={loading}
            disableColumnMenu
            showCellRightBorder
            showColumnRightBorder
            disableSelectionOnClick
            localeText={localeText}
            getRowHeight={() => 'auto'}
            components={{
              Pagination: CustomPagination,
              LoadingOverlay: LinearProgress,
            }}
          />
        </Grid>
      </Grid>
    </Card>
  );
};

const Shein = () => {
  const { hash } = useParams();
  const decrypt = decryptURL(hash);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [postLoading, setPostLoading] = useState(false);
  const [anuncio, setAnuncio] = useState(null);
  const { drops } = useContext(DropsContext);
  const { closeDialog } = useDialog();

  const loadData = async () => {
    try {
      setLoading(true);
      const params = {
        id: decrypt?.id,
        integra_access_id: decrypt?.integra_access_id,
      };
      const { data } = await api.get(`/Integracoes/Shein/Anuncio`, {
        params,
      });
      setAnuncio(data);
    } catch (error) {
    } finally {
      setLoading(false);
      closeDialog();
    }
  };

  const onSubmit = async (data) => {
    try {
      setPostLoading(true);
      await api.put(`/Integracoes/Shein/Anuncio`, data);
      loadData();
    } catch (error) {
    } finally {
      setPostLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const tabs = [
    {
      value: 0,
      key: 'Anúncio',
      label: 'Anúncio',
      icon: <Settings />,
    },
    {
      value: 1,
      key: 'Variações',
      label: 'Variações',
      icon: <Equalizer />,
    },
  ];

  const RenderTabs = () => {
    if (loading) return null;

    const tabProps = { anuncio, onSubmit, drops, loading: postLoading };

    switch (tab) {
      case 0:
        return <Anuncio {...tabProps} />;
      case 1:
        return <Variacoes {...tabProps} />;
      default:
        return null;
    }
  };

  return (
    <Container>
      <Header titulo="Editar Anúncio" />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Card>
            <Tabs
              variant="fullWidth"
              value={tab}
              onChange={(_, v) => setTab(v)}
              TabIndicatorProps={styles?.tabProps}
              sx={styles?.tab}
            >
              {tabs.map(({ show = true, ...t }) => show && <Tab {...t} />)}
            </Tabs>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <RenderTabs />
        </Grid>
      </Grid>
      <Fade in={loading}>
        <Backdrop open={loading} sx={styles?.loader}>
          <CircularProgress color="inherit" size={48} thickness={4} />
        </Backdrop>
      </Fade>
    </Container>
  );
};

export default Shein;
